/**
 * A formula: a field whose value is worked out from other fields, not typed.
 *
 * Section 5.5 of docs/records-platform-architecture.md names the three
 * computed types and fixes the function set: "arithmetic, `if`, `round`, date
 * difference, and string concatenation. No loops. No cross-row access." This
 * file is the `formula` half of that. `rollup` and `lookup` are not here.
 *
 * The vocabulary is CLOSED, and that is the design rather than a limitation.
 * `links.ts` says it three times over: "no brackets, no precedence, and the
 * evaluator is two nested loops rather than a parser", and "The closed table
 * IS the design: one renderer, one validator, and no expression anywhere for
 * anybody to parse." A typed formula string would need a grammar, a parser on
 * the server, a second parser on the device, and a promise that the two agree
 * forever. A tree needs one validator and one evaluator, and every screen can
 * read the tree back as words.
 *
 * The evaluator lives HERE and not in apps/server for the reason Section 5.7
 * rule 6 gives: "The device runs the same rule." One implementation, imported
 * by the write path and by the mirror, is the only way that promise can hold.
 * `links.ts` is the file this one is modelled on, line for line where it can be.
 *
 * A person may read three things, which is the whole freedom on offer:
 *
 *   a field on this row            {kind:'field'}
 *   a field on the row a reference points at   {kind:'hop'}
 *   a fixed value                  {kind:'const'}
 *   a total over the rows a multi_select holds   {kind:'agg'}
 *
 * A `hop` reaches a directory row (the price of an item) or a record row
 * (the rate on a contract), because a `reference` field points at either. It
 * reaches exactly one row and never two: depth one, the same rule a Link keeps.
 */

import type { ValueTypeId } from './generated/value-types';
import type { ReferenceReader } from './links';
import type { CoversAll } from './covers-all';

/* ------------------------------------------------------------------ the tree */

/**
 * What one node yields. A superset of the result types, because `date` may be
 * read and compared and subtracted, and never stored as an answer.
 */
export type FormulaValueType = 'number' | 'money' | 'quantity' | 'text' | 'boolean' | 'date';

/** What a formula field may BE. One of these decides its slot family. */
export type FormulaResultType = 'number' | 'money' | 'quantity' | 'text' | 'boolean';

/** The four arithmetic operators. There is no fifth, and no precedence to learn. */
export type FormulaCalcOp = 'add' | 'subtract' | 'multiply' | 'divide';

/** What an `if` may ask. Closed, exactly as a link condition's op set is closed. */
export type FormulaTestOp = 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'filled' | 'empty';

export const testNeedsRight = (op: FormulaTestOp): boolean => op !== 'filled' && op !== 'empty';

/** What an `agg` works out over the chosen rows. Closed, like the calc ops. */
export type FormulaAggOp = 'sum' | 'count' | 'min' | 'max';

export type FormulaNode =
  /** A field on this row. Its own record's field id. */
  | { kind: 'field'; fieldId: string }
  /**
   * A field on the row a reference points at.
   *
   * `via` is a `reference` field on THIS record. `fieldId` is a stored field on
   * the record `via` targets. This is the same shape `LinkMapping` already
   * carries as `{kind:'reference', via, fieldId}`, and the same shape
   * `app.field.default_expr` describes as `{from_reference, field}`.
   */
  | { kind: 'hop'; via: string; fieldId: string }
  | { kind: 'const'; value: number | string | boolean }
  | { kind: 'calc'; op: FormulaCalcOp; left: FormulaNode; right: FormulaNode }
  | { kind: 'round'; of: FormulaNode; decimals: number }
  | {
      kind: 'if';
      left: FormulaNode;
      op: FormulaTestOp;
      /** Absent on `filled` and `empty`, which ask about one side only. */
      right?: FormulaNode;
      then: FormulaNode;
      otherwise: FormulaNode;
    }
  | { kind: 'days_between'; from: FormulaNode; to: FormulaNode }
  | { kind: 'concat'; parts: readonly FormulaNode[] }
  /**
   * The first choice that is filled. "Litres, or else the customer's usual."
   *
   * It is the commonest pattern in every shop that keeps a usual quantity and
   * a usual rate, and it is expressible already: `if X filled then X else Y`
   * writes X twice and costs four nodes. As one node it is one row in the
   * builder, one line in the sentence, and one `COALESCE` in the card's SQL.
   *
   * At least two choices, and all of them in one family: a fallback that
   * answers a different kind of thing is a mistake, not a fallback.
   */
  | { kind: 'coalesce'; of: readonly FormulaNode[] }
  /**
   * A total over many rows: "the total of Price over Services", "the number
   * of Guests".
   *
   * `lines` is a field on THIS row that holds many rows, in one of two forms:
   *
   *   multi_select  the rows it holds are the rows of the list it points at,
   *                 chosen by id (the choices plan)
   *   lines         the rows are the lines directly under this row, in the
   *                 lines record its spec names (Decision 7 of the lines plan):
   *                 never the lines of another entry, never two levels down
   *
   * `of` is a field on those rows; `count` takes none and every other op
   * needs one. It may be a line formula, so a stay adds up the Guests each
   * room counted. A hop reaches one row and this reaches all of them, which
   * is why a hop through a multi_select is refused and this node is the only
   * way to read one.
   */
  | { kind: 'agg'; op: FormulaAggOp; lines: string; of?: string };

/** Every node kind, in the order of the type. */
export const FORMULA_NODE_KINDS = [
  'field',
  'hop',
  'const',
  'calc',
  'round',
  'if',
  'days_between',
  'concat',
  'coalesce',
  'agg',
] as const satisfies readonly FormulaNode['kind'][];
true satisfies CoversAll<FormulaNode['kind'], typeof FORMULA_NODE_KINDS>;

/**
 * What happens when a field the formula reads is empty.
 *
 * `blank` is the default and it is the safe one: the answer is nothing, and the
 * row draws a dash. A zero would be a number somebody adds up.
 *
 * `zero` is for the sums a person means to be forgiving: Subtotal plus Tax
 * minus Discount, where an unfilled Discount means no discount and not "we do
 * not know". It reads a missing NUMBER as 0 and nothing else, so an empty
 * reference still answers nothing.
 */
export type FormulaOnMissing = 'blank' | 'zero';

export type FormulaSpec = {
  result: FormulaResultType;
  expr: FormulaNode;
  onMissing: FormulaOnMissing;
};

/**
 * A dependent choice: offer only the rows of the target list whose `field`
 * equals this entry's `equals`. The Sub-category of an expense filters the
 * Sub-categories list by its Category.
 *
 *   field   a `reference` field on the TARGET list (Category on Sub-categories).
 *   equals  a `reference` or `multi_select` field on THIS row (Category on the
 *           expense). Both point at the same list. On a line, `equals` may
 *           instead name a field of the row the line hangs from — the entry
 *           at depth 1, the depth-1 line at depth 2 — by that field's own id
 *           (field ids are unique across records). The line reads it through
 *           its parent reference: the forms pass the parent's value beside
 *           the line's, and the server the parent's values in the request
 *           (Decision 2 of docs/plans/dependent-choices-and-multi-select.md,
 *           row "Inside lines").
 *
 * When `equals` is a multi_select, "equal" means "one of the chosen rows".
 * `rowMatches` is the rule, and `filterProblems` checks the shape.
 */
export type ChoiceFilter = { field: string; equals: string };

/** The spec of a `reference` or `multi_select` field. Every key is optional. */
export type ChoiceSpec = {
  filter?: ChoiceFilter;
  /** multi_select only: the most rows it may hold, 1 to `CHOICE_MAX_CHOSEN`. */
  maxChosen?: number;
};

/**
 * The spec of a `lines` field: the lines record its lines are entries of, the
 * `number` field on the same row whose value starts that many lines (null when
 * none does), and the most lines one row holds, 1 to `ENTRY_BATCH_MAX`.
 *
 * The server writes `recordId` when it makes the lines record, in the same
 * transaction as the field, and nobody changes it after.
 */
export type LinesFieldSpec = { recordId: string; countFrom: string | null; max: number };

/**
 * app.field.spec, keyed by field type the way 0001_control_plane.sql:149
 * requires: a formula holds its recipe, a `lines` field its lines record, and a
 * reference or multi_select may hold a filter and a limit. Read it through
 * `formulaOf`, `linesOf` and `choiceOf`.
 */
export type FieldSpec = { formula: FormulaSpec } | { lines: LinesFieldSpec } | ChoiceSpec;

/** The most rows one multi_select may hold. The planner refuses more. */
export const CHOICE_MAX_CHOSEN = 20;

/**
 * The formula a spec holds, or null when it holds none. Safe on a spec that
 * came off the wire unchecked: anything that is not an object holds nothing.
 */
export const formulaOf = (spec: FieldSpec | null | undefined): FormulaSpec | null =>
  typeof spec === 'object' && spec !== null && 'formula' in spec ? (spec.formula ?? null) : null;

/** The lines record a `lines` field's spec names, or null when it holds none. */
export const linesOf = (spec: FieldSpec | null | undefined): LinesFieldSpec | null =>
  typeof spec === 'object' && spec !== null && !Array.isArray(spec) && 'lines' in spec ? (spec.lines ?? null) : null;

/** The filter and limit a spec holds, or null when it is a formula's, a lines field's, or absent. */
export const choiceOf = (spec: FieldSpec | null | undefined): ChoiceSpec | null =>
  typeof spec !== 'object' || spec === null || Array.isArray(spec) || 'formula' in spec || 'lines' in spec
    ? null
    : spec;

/** The depth a tree may reach. A person who needs more needs a second field. */
export const FORMULA_MAX_DEPTH = 12;

/** How many nodes one tree may hold, so a paste can never make an unrunnable field. */
export const FORMULA_MAX_NODES = 64;

/* ------------------------------------------------------------- the evaluator */

/** A field-id keyed row, the shape both sides already pass around. */
export type FormulaValues = Readonly<Record<string, unknown>>;

/** What a formula can answer with. Null means "nothing", never zero. */
export type FormulaResult = string | number | boolean | null;

/**
 * How an `agg` node reaches its rows, by the id of the field that holds them:
 * the rows a `multi_select` on this entry holds, in the person's order, or the
 * lines directly under this row that a `lines` field holds, in their order and
 * already worked out. Each is its own values keyed by field id.
 *
 * The server loads them up front (`formulaLinesRead` says which), and the device
 * reads its mirror, so `evaluateFormula` stays synchronous on both.
 */
export type LinesReader = (fieldId: string) => readonly FormulaValues[];

/** True when the value is present in the sense `filled` means. */
const isFilled = (value: unknown): boolean =>
  value !== null &&
  value !== undefined &&
  value !== '' &&
  !(Array.isArray(value) && value.length === 0);

/**
 * A number, or null.
 *
 * `pg` hands `numeric` back as a string, so a string that reads as a number is
 * a number here. A boolean is NOT: true is not 1, and treating it as 1 would
 * let `Quantity × Paid` produce an amount nobody meant.
 */
const asNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const read = Number(value);
    return Number.isFinite(read) ? read : null;
  }
  return null;
};

const asText = (value: unknown): string | null => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return null;
};

/** Midnight UTC of a stored date or datetime, in milliseconds. Null when unreadable. */
const asDayMs = (value: unknown): number | null => {
  const text = typeof value === 'string' ? value : null;
  if (text === null || text === '') return null;
  // A bare date and a full instant both start with the same ten characters, and
  // a day difference must not move with the reader's clock. So only the day is
  // read, and it is read as UTC.
  const day = text.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const parsed = Date.parse(`${day}T00:00:00.000Z`);
  return Number.isFinite(parsed) ? parsed : null;
};

const DAY_MS = 86_400_000;

/**
 * Rounds to `decimals` places, half away from zero.
 *
 * A copy of `roundTo` in `packages/ui/src/fields/arithmetic.ts`, and it has to
 * be a copy: this package may not import that one. The reason for the decimal
 * shift is written there in full. Change one and change the other.
 */
export const roundTo = (value: number, decimals: number): number => {
  if (!Number.isFinite(value)) return value;
  const places = Math.min(8, Math.max(0, Math.trunc(decimals)));
  const literal = String(value);
  if (literal.includes('e') || literal.includes('E')) {
    const factor = 10 ** places;
    return Math.round(value * factor) / factor;
  }
  const shifted = Number(`${literal}e${places}`);
  if (!Number.isFinite(shifted)) return value;
  const rounded = shifted < 0 ? -Math.round(-shifted) : Math.round(shifted);
  const back = Number(`${rounded}e-${places}`);
  if (!Number.isFinite(back)) {
    const factor = 10 ** places;
    return Math.round(value * factor) / factor;
  }
  return back === 0 ? 0 : back;
};

/**
 * The answer, or null.
 *
 * Null is the honest answer for every case the rule cannot cover: an empty
 * field under `onMissing: 'blank'`, a division by zero, a reference that points
 * nowhere, a value that reads back as the wrong type. It is never a throw and
 * it is never a zero, because a zero is a number a total would swallow.
 *
 * `readHop` is how a `hop` node reaches the other row. The server loads the
 * pairs it needs up front, the way `loadReferenceValues` already does for a
 * Link; the device reads its mirror. Passing null refuses every hop, which is
 * what a preview with nothing loaded should do.
 *
 * `readLines` is how an `agg` node reaches the chosen rows, and passing null
 * answers every `agg` with nothing, for the same reason.
 */
export const evaluateFormula = (
  spec: FormulaSpec,
  values: FormulaValues,
  readHop: ReferenceReader | null = null,
  readLines: LinesReader | null = null,
): FormulaResult => {
  const answer = evalNode(spec.expr, values, { hop: readHop, lines: readLines }, spec.onMissing);
  if (answer === null) return null;
  return castResult(answer, spec.result);
};

/** The last step: the tree's answer, in the type the field declares. */
const castResult = (value: FormulaResult, result: FormulaResultType): FormulaResult => {
  if (result === 'text') return asText(value);
  if (result === 'boolean') return typeof value === 'boolean' ? value : null;
  const held = asNumber(value);
  return held === null ? null : held;
};

/** The two ways a node reaches past its own row. */
type Readers = { readonly hop: ReferenceReader | null; readonly lines: LinesReader | null };

const evalNode = (
  node: FormulaNode,
  values: FormulaValues,
  readers: Readers,
  onMissing: FormulaOnMissing,
): FormulaResult => {
  const readHop = readers.hop;
  switch (node.kind) {
    case 'field': {
      const held = values[node.fieldId];
      return isFilled(held) ? scalar(held) : null;
    }

    case 'hop': {
      const pointed = values[node.via];
      if (typeof pointed !== 'string' || pointed === '' || readHop === null) return null;
      const held = readHop(pointed, node.fieldId);
      return isFilled(held) ? scalar(held) : null;
    }

    case 'const':
      return node.value;

    case 'calc': {
      const left = numeric(evalNode(node.left, values, readers, onMissing), onMissing);
      const right = numeric(evalNode(node.right, values, readers, onMissing), onMissing);
      if (left === null || right === null) return null;
      if (node.op === 'add') return left + right;
      if (node.op === 'subtract') return left - right;
      if (node.op === 'multiply') return left * right;
      // A division by zero has no forgiving reading. `onMissing` does not reach
      // it, because zero is an answer the person gave rather than one they left out.
      if (right === 0) return null;
      const quotient = left / right;
      return Number.isFinite(quotient) ? quotient : null;
    }

    case 'round': {
      const held = numeric(evalNode(node.of, values, readers, onMissing), onMissing);
      return held === null ? null : roundTo(held, node.decimals);
    }

    case 'if': {
      const held = test(node, values, readers, onMissing);
      if (held === null) return null;
      return evalNode(held ? node.then : node.otherwise, values, readers, onMissing);
    }

    case 'days_between': {
      const from = asDayMs(evalNode(node.from, values, readers, onMissing));
      const to = asDayMs(evalNode(node.to, values, readers, onMissing));
      if (from === null || to === null) return null;
      return Math.round((to - from) / DAY_MS);
    }

    case 'concat': {
      // Forgiving on purpose, and it is the only forgiving node. A missing
      // middle name must not blank the whole name, and no total ever adds text.
      const parts = node.parts.map((part) => asText(evalNode(part, values, readers, onMissing)) ?? '');
      return parts.join('');
    }

    case 'coalesce': {
      // In order, and the first filled choice wins.
      //
      // `onMissing: 'zero'` is deliberately NOT applied before the choosing.
      // Zero is a filled value, so a forgiving formula would take the first
      // choice every time and every "or else" after it would be dead. The
      // zero belongs to the arithmetic above, which is where a figure nobody
      // filled in has to become a number.
      for (const part of node.of) {
        const held = evalNode(part, values, readers, onMissing);
        if (held !== null) return held;
      }
      return null;
    }

    case 'agg':
      return aggregate(node, readers.lines, onMissing);

    default:
      // An unknown kind refuses rather than guesses. A stored tree from a newer
      // version reaches here, and a wrong number would be worse than none.
      return null;
  }
};

/**
 * One `agg`, worked out.
 *
 * No reader is no answer. Zero rows count 0. Every other operation over zero
 * rows answers nothing, subject to `onMissing` (Decision 7 of the lines plan):
 * under `blank` it is blank, under `zero` it is 0. A row whose `of` is empty
 * follows `onMissing` the same way, exactly as an empty field in a sum does.
 */
const aggregate = (
  node: Extract<FormulaNode, { kind: 'agg' }>,
  readLines: LinesReader | null,
  onMissing: FormulaOnMissing,
): FormulaResult => {
  if (readLines === null) return null;
  const rows = readLines(node.lines);
  if (node.op === 'count') return rows.length;
  if (node.of === undefined) return null;
  const of = node.of;
  const held: number[] = [];
  for (const row of rows) {
    const one = row[of];
    const read = numeric(isFilled(one) ? scalar(one) : null, onMissing);
    if (read === null) return null;
    held.push(read);
  }
  if (held.length === 0) return numeric(null, onMissing);
  if (node.op === 'sum') return held.reduce((total, one) => total + one, 0);
  return node.op === 'min' ? Math.min(...held) : Math.max(...held);
};

/** One stored value, narrowed to what a formula can carry. A set cannot be read. */
const scalar = (value: unknown): FormulaResult => {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  return null;
};

/** A number for arithmetic, with the `onMissing` rule applied. */
const numeric = (value: FormulaResult, onMissing: FormulaOnMissing): number | null => {
  if (value === null) return onMissing === 'zero' ? 0 : null;
  return asNumber(value);
};

/** One `if` question, answered. Null when the question itself cannot be asked. */
const test = (
  node: Extract<FormulaNode, { kind: 'if' }>,
  values: FormulaValues,
  readers: Readers,
  onMissing: FormulaOnMissing,
): boolean | null => {
  const left = evalNode(node.left, values, readers, onMissing);
  if (node.op === 'filled') return left !== null;
  if (node.op === 'empty') return left === null;
  if (node.right === undefined) return null;
  const right = evalNode(node.right, values, readers, onMissing);
  if (left === null || right === null) return null;

  if (node.op === 'eq' || node.op === 'ne') {
    const same =
      typeof left === 'boolean' || typeof right === 'boolean'
        ? left === right
        : (asNumber(left) !== null && asNumber(right) !== null
            ? asNumber(left) === asNumber(right)
            : asText(left) === asText(right));
    return node.op === 'eq' ? same : !same;
  }

  // An ordering is asked of numbers, or of days. Nothing else has an order a
  // person would agree with, so nothing else is compared.
  const leftN = asNumber(left) ?? asDayMs(left);
  const rightN = asNumber(right) ?? asDayMs(right);
  if (leftN === null || rightN === null) return null;
  if (node.op === 'gt') return leftN > rightN;
  if (node.op === 'gte') return leftN >= rightN;
  if (node.op === 'lt') return leftN < rightN;
  return leftN <= rightN;
};

/* ------------------------------------------------------------- the type table */

/**
 * What each value type reads as, inside a formula.
 *
 * `select` and `reference` read as text on purpose: "if Payment mode is Cash"
 * is the commonest question a shop asks, and it is an equality on an id. They
 * are text and nothing else, so no arithmetic can reach them.
 *
 * A `multi_select` holds a set, an `attachment` holds files, and a `location`
 * holds a pair. None of the three has one scalar value, so none is readable and
 * the table leaves them out.
 */
const READS_AS: Readonly<Record<string, FormulaValueType>> = {
  number: 'number',
  money: 'money',
  quantity: 'quantity',
  text: 'text',
  long_text: 'text',
  phone: 'text',
  select: 'text',
  reference: 'text',
  date: 'date',
  datetime: 'date',
  boolean: 'boolean',
};

/** The types a formula may read at all. A picker offers exactly this set. */
export const readableFieldType = (type: string): FormulaValueType | null => READS_AS[type] ?? null;

const NUMERIC: ReadonlySet<FormulaValueType> = new Set(['number', 'money', 'quantity']);

export const isNumericType = (type: FormulaValueType): boolean => NUMERIC.has(type);

/**
 * What a declared result accepts.
 *
 * The three numeric results take any numeric tree, and that is deliberate. The
 * flavour of a number is often ambiguous inside a tree and never ambiguous to
 * the person: "3 litres times 28" is an amount, and "3 crates times 12" is a
 * count, and the two trees are the same shape. So the DECLARED result settles
 * it, and this table does not pretend to know better.
 *
 * The guard that matters is not here. It is `calcType`, which refuses an amount
 * times an amount and a plain number added to an amount. Those are the mistakes
 * that produce a wrong figure; a flavour this table could not have inferred is not.
 *
 * `text` takes everything, because writing a number into a sentence is the
 * point of `concat`. `boolean` takes nothing else: a number is not a yes.
 */
const NUMERIC_RESULTS: readonly FormulaValueType[] = ['number', 'money', 'quantity'];

const RESULT_FITS: Readonly<Record<FormulaResultType, readonly FormulaValueType[]>> = {
  number: NUMERIC_RESULTS,
  money: NUMERIC_RESULTS,
  quantity: NUMERIC_RESULTS,
  text: ['text', 'number', 'money', 'quantity', 'boolean', 'date'],
  boolean: ['boolean'],
};

export const resultAccepts = (result: FormulaResultType, value: FormulaValueType): boolean =>
  RESULT_FITS[result].includes(value);

/** Which slot family a result draws from. The planner needs this and nothing else. */
export const slotFamilyFor = (result: FormulaResultType): 'n' | 't' | 'b' => {
  if (result === 'text') return 't';
  if (result === 'boolean') return 'b';
  return 'n';
};

/**
 * What a formula must work out to, to fill a field of this type.
 *
 * `RESULT_FITS` read backwards: a money field takes an amount, a phone field
 * takes words, and a date field takes nothing at all because no formula RESULT
 * is a date. Null means the type cannot be filled by a worked-out answer, and a
 * link screen or a "starts with" sheet then does not offer one.
 *
 * `select` and `reference` are absent on purpose, and it is not an oversight:
 * both are stored as an id, and an id is not a value a person can compute. They
 * take a default (`DEFAULT_TAKERS` holds both) but only as a named row or a
 * named choice, which is a shape rule rather than a result rule, and
 * `defaultProblems` keeps it.
 */
const RESULT_FOR_FIELD: Readonly<Record<string, FormulaResultType>> = {
  money: 'money',
  number: 'number',
  quantity: 'quantity',
  text: 'text',
  long_text: 'text',
  phone: 'text',
  boolean: 'boolean',
};

export const formulaResultFor = (fieldType: string): FormulaResultType | null =>
  RESULT_FOR_FIELD[fieldType] ?? null;

/* --------------------------------------------------------------- validation */

export type FormulaProblemCode =
  /** The stored document is not a tree this version can read. */
  | 'FORMULA_SHAPE'
  /** More nesting, or more nodes, than one field may hold. */
  | 'FORMULA_TOO_BIG'
  /** A node names a field this record does not have. */
  | 'FORMULA_FIELD_UNKNOWN'
  /** A node reads a field nothing stores. Depth stays one. */
  | 'FORMULA_FIELD_COMPUTED'
  /** A field this formula cannot read at all: a set, a file, a place. */
  | 'FORMULA_FIELD_UNREADABLE'
  /** `via` is not a reference field, so there is nothing to hop through. */
  | 'FORMULA_HOP_NOT_REFERENCE'
  /** The hop lands on a computed field. One hop, onto something stored. */
  | 'FORMULA_HOP_NOT_STORED'
  /**
   * A line formula reads a worked-out field of its parent or its root
   * (Decision 6 of the lines plan). A formula above may total the lines below
   * it, so a line that read one would make a ring across two records, which
   * no walk of one record's formulas can see. A line reads only what was typed
   * above it.
   */
  | 'FORMULA_PARENT_COMPUTED'
  /** The formula reads itself, directly or round a ring of other formulas. */
  | 'FORMULA_CYCLE'
  /** A flat wire holds two nodes with one id. */
  | 'FORMULA_NODE_DUPLICATE'
  /** A flat wire names a node it never gives. */
  | 'FORMULA_NODE_UNKNOWN'
  /** A flat wire gives a node nothing reaches. */
  | 'FORMULA_NODE_UNUSED'
  /** A flat wire's root names no node at all. */
  | 'FORMULA_ROOT_UNKNOWN'
  /** An operator was handed something it cannot work on. */
  | 'FORMULA_TYPE_MISMATCH'
  /** The tree answers one type and the field declares another. */
  | 'FORMULA_RESULT_MISMATCH'
  /**
   * An `agg` somewhere other than a formula field: a default or a link mapping.
   * Only a formula field's fill loads the chosen rows, so anywhere else the
   * total would be worked out from nothing.
   */
  | 'FORMULA_AGG_NOT_HERE'
  /** A filter or a limit on a field that is not a reference or a multi_select. */
  | 'FILTER_NOT_CHOICE'
  /** The filter's `field` is not on the list this field points at. */
  | 'FILTER_FIELD_UNKNOWN'
  /** The filter's `field` is not a reference field on that list. */
  | 'FILTER_FIELD_NOT_REFERENCE'
  /** The filter's `equals` is not on this record. */
  | 'FILTER_EQUALS_UNKNOWN'
  /** The filter's `equals` is not a reference or a multi_select. */
  | 'FILTER_EQUALS_NOT_CHOICE'
  /** `field` and `equals` point at different lists. */
  | 'FILTER_LIST_MISMATCH'
  /** The field filters by itself, directly or round a ring of other filters. */
  | 'FILTER_CYCLE'
  /** `maxChosen` is not a whole number from 1 to 20, or is on a reference. */
  | 'FILTER_MAX_CHOSEN';

export type FormulaProblem = { code: FormulaProblemCode; fieldId?: string; message: string };

/**
 * What one live field looks like to the checker. The caller builds these.
 *
 * `result` is set on a `formula` field and on nothing else. It is what makes a
 * formula readable by another formula: the answer has been kept in a slot since
 * migration 0011, so reading it is reading a stored value, and the caller that
 * knows the field also knows what it works out to. A `formula` field with no
 * `result` is refused, because a checker that guessed the type would let a
 * count be added to an amount.
 */
export type FormulaFieldInfo = {
  readonly id: string;
  readonly label: string;
  readonly type: string;
  /** `formula` only: the type it works out to. */
  readonly result?: FormulaResultType;
};

/**
 * How the checker asks about a field.
 *
 * `own` answers for this record. `hop` answers for the record a reference field
 * points at, and answers null when `via` is not a reference at all. Both are
 * synchronous, so the same checker runs on the phone with the schema in hand
 * and on the server with the rows already loaded.
 */
export type FormulaLookup = {
  readonly own: (fieldId: string) => FormulaFieldInfo | null;
  readonly hop: (viaFieldId: string, fieldId: string) => FormulaFieldInfo | null;
  /** True when `via` is a live `reference` field on this record. */
  readonly isReference: (fieldId: string) => boolean;
  /**
   * The formula behind a field on THIS record, when it has one.
   *
   * Only the cycle walk uses it. A formula may now read another formula, so
   * "does this field read itself" is no longer a question one tree can answer:
   * Total reads Line total, Line total reads Total, and neither tree names
   * itself. The walk follows this function until it comes back round or runs
   * out. Absent means the caller cannot answer, and then only the direct
   * self-read is refused — which is the answer every caller gave before.
   */
  readonly formulaOf?: (fieldId: string) => FormulaSpec | null;
  /**
   * The choice ids a select field on THIS record offers.
   *
   * Only `defaultProblems` asks. A select starts with one of its OWN choices,
   * so the recipe has to be held against the list the field actually offers,
   * and an id is the only thing an entry ever stores. Absent, or null for a
   * field the caller cannot answer for, means the ids are not checked — the
   * shape still is.
   */
  readonly optionsOf?: (fieldId: string) => readonly string[] | null;
  /**
   * The record a reference field points at.
   *
   * One function for two questions, because they are one question asked of two
   * records. With no `viaFieldId` the field is on THIS record; with one, the
   * field is on the record `via` points at, which is where a hop lands. Null
   * for anything that is not a live reference field.
   *
   * Only `defaultProblems` asks: a reference may start with a row on the list
   * it points at, and "the same list" is the whole rule.
   */
  readonly targetOf?: (fieldId: string, viaFieldId?: string) => string | null;
  /**
   * A field on the rows a field of THIS record holds: what an `agg` reads on
   * each one. For a `multi_select`, a field of the list it points at; for a
   * `lines` field, a field of the lines record its spec names
   * (`spec.lines.recordId`), line formulas included. Null when `linesFieldId`
   * is neither, or the field is not on those rows. Absent means the caller
   * cannot answer, and every `agg` that reads a field is refused.
   */
  readonly lineField?: (linesFieldId: string, fieldId: string) => FormulaFieldInfo | null;
  /**
   * True when `viaFieldId` is the `parent` or the `root` field of a lines
   * record and `fieldId` is a worked-out field on the row it reaches: a
   * formula, a rollup or a lookup. Only a lines record answers it; absent
   * means this record has no rows above it, and a hop is judged as any hop.
   */
  readonly isParentComputed?: (viaFieldId: string, fieldId: string) => boolean;
};

const problem = (code: FormulaProblemCode, message: string, fieldId?: string): FormulaProblem =>
  fieldId === undefined ? { code, message } : { code, message, fieldId };

/**
 * Every problem with one formula, at once.
 *
 * Never first-problem-wins: a builder screen fixes one round trip, not one
 * mistake per round trip. That is the rule `entry-values.ts` and `links.ts`
 * both already keep.
 *
 * `selfFieldId` is the field being defined, when there is one. A tree that
 * reads it is a cycle of length one, and it is refused by name.
 */
export const checkFormula = (
  spec: FormulaSpec,
  lookup: FormulaLookup,
  selfFieldId: string | null = null,
): readonly FormulaProblem[] => {
  const problems: FormulaProblem[] = [];
  const size = measure(spec.expr);
  if (size.depth > FORMULA_MAX_DEPTH || size.nodes > FORMULA_MAX_NODES) {
    problems.push(
      problem(
        'FORMULA_TOO_BIG',
        `A formula may hold ${FORMULA_MAX_NODES} pieces nested ${FORMULA_MAX_DEPTH} deep. This one holds ${size.nodes} nested ${size.depth} deep.`,
      ),
    );
    return problems;
  }

  ringThroughFormulas(spec, lookup, selfFieldId, problems);

  const answered = typeOf(spec.expr, lookup, selfFieldId, problems);
  if (answered !== null && !resultAccepts(spec.result, answered)) {
    problems.push(
      problem(
        'FORMULA_RESULT_MISMATCH',
        `This works out to ${answered}, and the field is set to ${spec.result}.`,
      ),
    );
  }
  return problems;
};

/**
 * A ring that goes out through other formulas and comes back.
 *
 * Total reads Line total and Line total reads Total: neither tree names itself,
 * and both are refused here rather than at Save. The walk is over the record's
 * OWN field reads only, because a hop lands on another record and a formula
 * there cannot read a field here. A field with no formula behind it ends its
 * branch, which is why the walk terminates on a record of any size.
 *
 * `seen` is shared across the starts on purpose: "this field does not reach
 * me" is a property of the field and not of the path taken to it, so once a
 * branch has been walked it is never walked again.
 */
const ringThroughFormulas = (
  spec: FormulaSpec,
  lookup: FormulaLookup,
  selfFieldId: string | null,
  problems: FormulaProblem[],
): void => {
  const formulaOf = lookup.formulaOf;
  if (formulaOf === undefined || selfFieldId === null) return;

  const seen = new Set<string>();
  const reaches = (fieldId: string): readonly string[] | null => {
    if (fieldId === selfFieldId) return [fieldId];
    if (seen.has(fieldId)) return null;
    seen.add(fieldId);
    const held = formulaOf(fieldId);
    if (held === null) return null;
    for (const next of formulaFieldsRead(held)) {
      const found = reaches(next);
      if (found !== null) return [fieldId, ...found];
    }
    return null;
  };

  const nameOf = (fieldId: string): string => `"${lookup.own(fieldId)?.label ?? fieldId}"`;
  for (const start of formulaFieldsRead(spec)) {
    // The direct self-read is a ring of length one, and `typeOf` names it.
    if (start === selfFieldId) continue;
    const ring = reaches(start);
    if (ring === null) continue;
    const names = [selfFieldId, ...ring].map(nameOf);
    problems.push(
      problem(
        'FORMULA_CYCLE',
        `${names[0]} reads ${names.slice(1).join(', which reads ')}, and that is a circle.`,
        start,
      ),
    );
  }
};

/* ------------------------------------------------------- what a field starts with */

/**
 * The types a field may start with a worked-out value on.
 *
 * The five that are missing are missing for one reason each, and none of them
 * is a preference. A `date` and a `datetime` have a picker that already opens
 * on today, which is the only default anybody has ever asked for and is not a
 * recipe. A `multi_select`, an `attachment` and a `location` hold no single
 * value, so there is nothing for a tree to answer with — the same reason
 * `READS_AS` leaves all three out. A `formula`, a `rollup` and a `lookup` are
 * worked out every time the row is saved; a starting value would be overwritten
 * by the thing that computes them.
 */
export const DEFAULT_TAKERS: readonly ValueTypeId[] = [
  'money',
  'number',
  'quantity',
  'text',
  'long_text',
  'phone',
  'boolean',
  'select',
  'reference',
];

/** Why one type takes no starting value, in the words a person reads. */
const NO_DEFAULT_WORD: Readonly<Record<string, string>> = {
  date: "A date keeps the picker's own today, and takes no recipe.",
  datetime: "A time keeps the picker's own now, and takes no recipe.",
  multi_select: 'A field that holds several choices takes no starting value.',
  attachment: 'A file cannot be worked out, so this field takes no starting value.',
  location: 'A place cannot be worked out, so this field takes no starting value.',
  formula: 'This field is worked out every time, so it needs nothing to start with.',
  rollup: 'This field is worked out every time, so it needs nothing to start with.',
  lookup: 'This field is worked out every time, so it needs nothing to start with.',
};

/**
 * What the caller knows about the field being defined that the lookup cannot
 * answer for: a field being ADDED has no id yet, so nothing can be looked up
 * about it.
 *
 * Both are answered by `lookup.optionsOf` and `lookup.targetOf` for a field that
 * already exists, and given here for one that does not. Given wins, because the
 * caller is describing the field as it will be after the save.
 */
export type DefaultOptions = {
  /** The choice ids the select being defined offers. */
  readonly optionIds?: readonly string[] | null;
  /** The record the reference being defined points at. */
  readonly targetRecordId?: string | null;
};

/**
 * Every `agg` in a tree that is not a formula field's, refused.
 *
 * Only the fill of a formula FIELD loads the rows a multi select holds (the
 * server up front, the phone from its mirror). A default and a link mapping are
 * worked out with no rows reader, so a total there would answer nothing, or,
 * under `onMissing: 'zero'`, a confident wrong figure. The lines plan says the
 * same in its Decision 7: an `agg` is allowed only in a formula field.
 */
export const aggProblems = (spec: FormulaSpec): readonly FormulaProblem[] =>
  formulaLinesRead(spec).map((one) =>
    problem('FORMULA_AGG_NOT_HERE', 'Only a worked-out field can total or count the rows a field holds.', one.lines),
  );

/** True when a field of this type keeps a value nobody typed. */
export const isComputedType = (type: string): boolean =>
  type === 'formula' || type === 'rollup' || type === 'lookup';

/**
 * Every problem with what a field STARTS WITH, at once.
 *
 * A default is the same tree a worked-out field keeps, so the first thing it
 * gets is the same check: `checkFormula` proves it reads fields that exist, is
 * not a ring, fits in the size a field may hold, and answers the type it
 * declares. What this function adds is the three ways a DEFAULT differs from a
 * formula.
 *
 * 1. Not every type takes one. `DEFAULT_TAKERS` says which do, and a type
 *    outside it is refused with a plain sentence rather than a rule number.
 * 2. A default reads what is TYPED, never what is worked out. A formula reads
 *    the row and a default fills the row, so if a default could read a formula
 *    the two would wait on each other: the formula wants the filled value, the
 *    fill wants the formula's answer. Refusing the read is what keeps the order
 *    a straight line. A hop is untouched by this — it lands on another row,
 *    whose own values were worked out and stored long before this form opened.
 * 3. The answer has to FIT the field. For everything with a value that fits in
 *    `RESULT_FOR_FIELD` the fit is the declared result, because the declared
 *    result is what `evaluateFormula` casts the answer to and therefore what
 *    lands in the slot. For a `select` and a `reference` the fit is a shape:
 *    an id is not something arithmetic can produce, so the only honest recipes
 *    are "this choice", "this row", and "the row the list it points at names".
 */
export const defaultProblems = (
  fieldType: string,
  spec: FormulaSpec,
  lookup: FormulaLookup,
  selfFieldId: string | null = null,
  options: DefaultOptions = {},
): readonly FormulaProblem[] => {
  const problems: FormulaProblem[] = [...checkFormula(spec, lookup, selfFieldId), ...aggProblems(spec)];

  if (!(DEFAULT_TAKERS as readonly string[]).includes(fieldType)) {
    problems.push(
      problem(
        'FORMULA_RESULT_MISMATCH',
        NO_DEFAULT_WORD[fieldType] ?? 'A field of this kind takes no starting value.',
      ),
    );
    // No fit rule below can mean anything for a field that takes no default at
    // all, and a second sentence about types would only bury the first.
    return problems;
  }

  for (const fieldId of formulaFieldsRead(spec)) {
    const field = lookup.own(fieldId);
    if (field !== null && isComputedType(field.type)) {
      problems.push(
        problem(
          'FORMULA_FIELD_COMPUTED',
          'A field that starts with a value reads what is typed, not what is worked out.',
          fieldId,
        ),
      );
    }
  }

  if (fieldType === 'select') {
    const node = spec.expr;
    if (node.kind !== 'const' || typeof node.value !== 'string' || node.value === '') {
      problems.push(
        problem(
          'FORMULA_RESULT_MISMATCH',
          'A field of choices starts with one of its own choices, and with nothing else.',
        ),
      );
    } else {
      const held = options.optionIds ?? (selfFieldId === null ? null : lookup.optionsOf?.(selfFieldId) ?? null);
      if (held !== null && !held.includes(node.value)) {
        problems.push(
          problem('FORMULA_RESULT_MISMATCH', 'That choice is not one this field offers.'),
        );
      }
    }
    return problems;
  }

  if (fieldType === 'reference') {
    referenceDefault(spec, lookup, selfFieldId, options, problems);
    return problems;
  }

  // A number, an amount, a quantity, words, or a yes-or-no. `spec.result` is
  // what the answer is cast to before it is stored, so it is what has to fit
  // the field; `checkFormula` above has already fitted the TREE to `result`.
  const wanted = formulaResultFor(fieldType);
  if (wanted !== null && !resultAccepts(wanted, spec.result)) {
    problems.push(
      problem(
        'FORMULA_RESULT_MISMATCH',
        `This works out to ${spec.result}, and this field holds ${wanted}.`,
      ),
    );
  }
  return problems;
};

/**
 * What a field that reads a list may start with.
 *
 * Two shapes, and no third. A `const` is one row of that list, named by its
 * uuid: the picker writes it, and this side has not loaded the rows of that
 * list, so any id passes and a row that has since gone simply draws nothing —
 * the same answer an emptied reference gives. A `hop` is "the customer's usual
 * item": a reference
 * field on the row this one points at, pointing at the SAME list. Anything else
 * would put an id from one list into a field that reads another, and the form
 * would show a blank where a name should be.
 */
const referenceDefault = (
  spec: FormulaSpec,
  lookup: FormulaLookup,
  selfFieldId: string | null,
  options: DefaultOptions,
  problems: FormulaProblem[],
): void => {
  const node = spec.expr;
  if (node.kind === 'const') {
    if (typeof node.value !== 'string' || node.value === '') {
      problems.push(
        problem('FORMULA_RESULT_MISMATCH', 'A field that reads a list starts with one row of it.'),
      );
    }
    return;
  }
  if (node.kind !== 'hop') {
    problems.push(
      problem(
        'FORMULA_RESULT_MISMATCH',
        'A field that reads a list starts with one row of it, or with the row another list names.',
      ),
    );
    return;
  }

  const landed = lookup.hop(node.via, node.fieldId);
  // A `via` that is not a reference, and a field that is not on the record it
  // points at, are both refused by `checkFormula` already and by name.
  if (landed === null) return;
  if (landed.type !== 'reference') {
    problems.push(
      problem(
        'FORMULA_RESULT_MISMATCH',
        `"${landed.label}" does not read a list, so it cannot name a row for this field.`,
        node.fieldId,
      ),
    );
    return;
  }

  const mine =
    options.targetRecordId ?? (selfFieldId === null ? null : lookup.targetOf?.(selfFieldId) ?? null);
  const there = lookup.targetOf?.(node.fieldId, node.via) ?? null;
  if (mine === null || there === null) {
    // Nothing was loaded to compare, so the honest answer is to refuse rather
    // than to let an id from an unknown list through.
    problems.push(
      problem(
        'FORMULA_RESULT_MISMATCH',
        `"${landed.label}" reads a list this field cannot be matched against.`,
        node.fieldId,
      ),
    );
    return;
  }
  if (mine !== there) {
    problems.push(
      problem(
        'FORMULA_RESULT_MISMATCH',
        `"${landed.label}" reads a different list, and a row of one list cannot fill a field that reads another.`,
        node.fieldId,
      ),
    );
  }
};

/* ------------------------------------------------------------- when a field shows */

/**
 * Every problem with a field's "show when" rule, at once.
 *
 * A show-when rule reads what a default may read (Decision 11 of the lines
 * plan): the typed fields of the row and one hop. So it gets the same checks
 * a default gets — `checkFormula` for the tree, `aggProblems` because no rows
 * reader runs beside it, and the refusal of a worked-out field, for the same
 * reason a default refuses one: the form decides what shows before the
 * formulas are worked out, and the server runs it before the required check.
 * What it adds is the one rule of its own: the answer is a yes or a no.
 */
export const showWhenProblems = (
  spec: FormulaSpec,
  lookup: FormulaLookup,
  selfFieldId: string | null = null,
): readonly FormulaProblem[] => {
  const problems: FormulaProblem[] = [...checkFormula(spec, lookup, selfFieldId), ...aggProblems(spec)];
  if (spec.result !== 'boolean') {
    problems.push(
      problem('FORMULA_RESULT_MISMATCH', `When a field shows is a yes or a no, and this works out to ${spec.result}.`),
    );
  }
  for (const fieldId of formulaFieldsRead(spec)) {
    const field = lookup.own(fieldId);
    if (field !== null && isComputedType(field.type)) {
      problems.push(
        problem(
          'FORMULA_FIELD_COMPUTED',
          'When a field shows reads what is typed, not what is worked out.',
          fieldId,
        ),
      );
    }
  }
  return problems;
};

/**
 * Whether a field shows on the form, for these values (Decision 11).
 *
 * A field with no rule always shows. A field with one shows only when the rule
 * answers yes: a rule that answers nothing — an empty field it reads, a hop
 * with nothing loaded — hides it, because "show the Guests when Status is
 * Checked in" means hidden until somebody says Checked in.
 *
 * A hidden field saves no value and is never required, on the phone, on the
 * web and on the server, which is why all three run this one function.
 */
export const isShown = (
  field: { readonly showWhen?: FormulaSpec | null },
  values: FormulaValues,
  readHop: ReferenceReader | null = null,
): boolean => {
  const rule = field.showWhen ?? null;
  if (rule === null) return true;
  return evaluateFormula(rule, values, readHop) === true;
};

/**
 * The fields of one row that do not show, for these values.
 *
 * A rule reads the row as the form shows it, so a field another rule hides
 * reads as empty: the form clears it as it hides. The answer is worked out
 * again with those fields blanked until it holds still. A ring among the rules
 * is refused when a field is made; one that reaches here anyway stops after
 * one round per field rather than running on.
 */
export const hiddenByShowWhen = (
  fields: readonly { readonly id: string; readonly showWhen?: FormulaSpec | null }[],
  values: FormulaValues,
  readHop: ReferenceReader | null = null,
): ReadonlySet<string> => {
  const ruled = fields.filter((field) => (field.showWhen ?? null) !== null);
  let hidden = new Set<string>();
  if (ruled.length === 0) return hidden;
  for (let round = 0; round <= ruled.length; round += 1) {
    const picture: Record<string, unknown> = { ...values };
    for (const id of hidden) picture[id] = null;
    const next = new Set(ruled.filter((field) => !isShown(field, picture, readHop)).map((field) => field.id));
    const same = next.size === hidden.size && [...next].every((id) => hidden.has(id));
    hidden = next;
    if (same) break;
  }
  return hidden;
};

const measure = (node: FormulaNode): { depth: number; nodes: number } => {
  const kids = childrenOf(node);
  if (kids.length === 0) return { depth: 1, nodes: 1 };
  let depth = 0;
  let nodes = 1;
  for (const kid of kids) {
    const held = measure(kid);
    depth = Math.max(depth, held.depth);
    nodes += held.nodes;
  }
  return { depth: depth + 1, nodes };
};

const childrenOf = (node: FormulaNode): readonly FormulaNode[] => {
  if (node.kind === 'calc') return [node.left, node.right];
  if (node.kind === 'round') return [node.of];
  if (node.kind === 'if') {
    return node.right === undefined
      ? [node.left, node.then, node.otherwise]
      : [node.left, node.right, node.then, node.otherwise];
  }
  if (node.kind === 'days_between') return [node.from, node.to];
  if (node.kind === 'concat') return node.parts;
  if (node.kind === 'coalesce') return node.of;
  return [];
};

/** The type one node answers with, or null when it answers nothing readable. */
const typeOf = (
  node: FormulaNode,
  lookup: FormulaLookup,
  selfFieldId: string | null,
  problems: FormulaProblem[],
): FormulaValueType | null => {
  switch (node.kind) {
    case 'field': {
      if (selfFieldId !== null && node.fieldId === selfFieldId) {
        problems.push(problem('FORMULA_CYCLE', 'A field cannot read itself.', node.fieldId));
        return null;
      }
      const field = lookup.own(node.fieldId);
      if (field === null) {
        problems.push(problem('FORMULA_FIELD_UNKNOWN', 'That field is not on this record.', node.fieldId));
        return null;
      }
      return readable(field, problems);
    }

    case 'hop': {
      // A multi_select reaches many rows and a hop reaches one, so it is
      // refused here by its type, whatever the caller's `isReference` says.
      if (lookup.own(node.via)?.type === 'multi_select' || !lookup.isReference(node.via)) {
        problems.push(
          problem('FORMULA_HOP_NOT_REFERENCE', 'A value can only be read through a field that points at a list.', node.via),
        );
        return null;
      }
      const field = lookup.hop(node.via, node.fieldId);
      if (field === null) {
        problems.push(problem('FORMULA_FIELD_UNKNOWN', 'That field is not on the record it points at.', node.fieldId));
        return null;
      }
      // Before `readable`, which would let a hop land on a formula with a
      // result: up the tree of lines, only a typed field may be read.
      if (lookup.isParentComputed?.(node.via, node.fieldId) === true) {
        problems.push(
          problem(
            'FORMULA_PARENT_COMPUTED',
            `"${field.label}" is worked out from the lines, so a line can only read what was typed above it.`,
            node.fieldId,
          ),
        );
        return null;
      }
      return readable(field, problems);
    }

    case 'const':
      if (typeof node.value === 'number') return 'number';
      if (typeof node.value === 'boolean') return 'boolean';
      return 'text';

    case 'calc': {
      const left = typeOf(node.left, lookup, selfFieldId, problems);
      const right = typeOf(node.right, lookup, selfFieldId, problems);
      if (left === null || right === null) return null;
      if (!isNumericType(left) || !isNumericType(right)) {
        problems.push(problem('FORMULA_TYPE_MISMATCH', `${left} and ${right} cannot be worked on with arithmetic.`));
        return null;
      }
      return calcType(node.op, left, right, problems);
    }

    case 'round': {
      const held = typeOf(node.of, lookup, selfFieldId, problems);
      if (held === null) return null;
      if (!isNumericType(held)) {
        problems.push(problem('FORMULA_TYPE_MISMATCH', `Only a number can be rounded, not ${held}.`));
        return null;
      }
      return held;
    }

    case 'if': {
      const left = typeOf(node.left, lookup, selfFieldId, problems);
      if (testNeedsRight(node.op)) {
        if (node.right === undefined) {
          problems.push(problem('FORMULA_SHAPE', `"${node.op}" needs something to compare against.`));
        } else {
          const right = typeOf(node.right, lookup, selfFieldId, problems);
          if (left !== null && right !== null && !comparable(node.op, left, right)) {
            problems.push(problem('FORMULA_TYPE_MISMATCH', `${left} and ${right} cannot be compared.`));
          }
        }
      }
      const then = typeOf(node.then, lookup, selfFieldId, problems);
      const otherwise = typeOf(node.otherwise, lookup, selfFieldId, problems);
      if (then === null || otherwise === null) return null;
      if (then === otherwise) return then;
      // Two numeric branches merge to the richer flavour, so a branch that is an
      // amount makes the whole answer an amount rather than a bare number.
      if (isNumericType(then) && isNumericType(otherwise)) return richerNumber(then, otherwise);
      problems.push(
        problem('FORMULA_TYPE_MISMATCH', `One way answers ${then} and the other answers ${otherwise}.`),
      );
      return null;
    }

    case 'days_between': {
      const from = typeOf(node.from, lookup, selfFieldId, problems);
      const to = typeOf(node.to, lookup, selfFieldId, problems);
      if (from === null || to === null) return null;
      if (from !== 'date' || to !== 'date') {
        problems.push(problem('FORMULA_TYPE_MISMATCH', 'Days between needs two dates.'));
        return null;
      }
      return 'number';
    }

    case 'concat': {
      for (const part of node.parts) typeOf(part, lookup, selfFieldId, problems);
      return 'text';
    }

    case 'coalesce': {
      // One family, and every choice in it. "Litres or else 'ask him'" is not
      // a fallback, it is two different answers wearing one field, and the
      // person who wrote it meant one of them.
      const held: FormulaValueType[] = [];
      let unreadable = false;
      for (const part of node.of) {
        const one = typeOf(part, lookup, selfFieldId, problems);
        if (one === null) unreadable = true;
        else held.push(one);
      }
      if (node.of.length < 2) {
        problems.push(problem('FORMULA_SHAPE', 'An "or else" needs a second choice to fall back on.'));
        return null;
      }
      if (unreadable || held.length === 0) return null;
      let merged = held[0] as FormulaValueType;
      for (const one of held.slice(1)) {
        if (one === merged) continue;
        // Two numeric choices merge to the richer flavour, exactly as the two
        // ways of an `if` do: a choice that is an amount makes it an amount.
        if (isNumericType(merged) && isNumericType(one)) {
          merged = richerNumber(merged, one);
          continue;
        }
        problems.push(
          problem('FORMULA_TYPE_MISMATCH', `One choice answers ${merged} and another answers ${one}.`),
        );
        return null;
      }
      return merged;
    }

    case 'agg':
      return aggType(node, lookup, problems);

    default:
      problems.push(problem('FORMULA_SHAPE', 'This formula holds a piece this version does not know.'));
      return null;
  }
};

/**
 * A field the formula may read, or a problem naming why it may not.
 *
 * A `formula` field is readable when the caller says what it works out to. Its
 * answer has been kept in a slot since migration 0011, so reading it is reading
 * a stored value and the fill order is what keeps it honest: `formulaOrder`
 * runs the one that is read before the one that reads it, on both sides.
 *
 * `rollup` and `lookup` stay refused. Neither has a slot, so there is nothing
 * on the row to read, and Section 5.5 keeps them out of this file entirely.
 */
const readable = (field: FormulaFieldInfo, problems: FormulaProblem[]): FormulaValueType | null => {
  if (field.type === 'formula') {
    if (field.result !== undefined) return field.result;
    problems.push(
      problem(
        'FORMULA_FIELD_COMPUTED',
        `"${field.label}" is worked out too, and what it works out to is not known here.`,
        field.id,
      ),
    );
    return null;
  }
  if (field.type === 'rollup' || field.type === 'lookup') {
    problems.push(
      problem(
        'FORMULA_FIELD_COMPUTED',
        `"${field.label}" is worked out too, and a formula reads only what is stored.`,
        field.id,
      ),
    );
    return null;
  }
  const held = readableFieldType(field.type);
  if (held === null) {
    problems.push(
      problem('FORMULA_FIELD_UNREADABLE', `"${field.label}" holds no single value a formula can read.`, field.id),
    );
    return null;
  }
  return held;
};

/**
 * What an `agg` answers: a count is a number, and a total, a lowest or a
 * highest is whatever the field it reads is (a total of prices is an amount).
 */
const aggType = (
  node: Extract<FormulaNode, { kind: 'agg' }>,
  lookup: FormulaLookup,
  problems: FormulaProblem[],
): FormulaValueType | null => {
  const lines = lookup.own(node.lines);
  if (lines === null) {
    problems.push(problem('FORMULA_FIELD_UNKNOWN', 'That field is not on this record.', node.lines));
    return null;
  }
  // Two fields hold many rows: a multi_select holds chosen rows of a list,
  // and a `lines` field holds the lines under this row. Nothing else does,
  // so an `agg` over anything else has nothing to total.
  if (lines.type !== 'multi_select' && lines.type !== 'lines') {
    problems.push(
      problem('FORMULA_TYPE_MISMATCH', `"${lines.label}" does not hold several rows, so nothing can be totalled over it.`, node.lines),
    );
    return null;
  }
  if (node.op === 'count') {
    if (node.of !== undefined) {
      problems.push(problem('FORMULA_SHAPE', 'A count reads no field.', node.of));
      return null;
    }
    return 'number';
  }
  if (node.of === undefined) {
    problems.push(problem('FORMULA_SHAPE', 'A total needs a field to read on each row.', node.lines));
    return null;
  }
  const field = lookup.lineField?.(node.lines, node.of) ?? null;
  if (field === null) {
    problems.push(problem('FORMULA_FIELD_UNKNOWN', 'That field is not on the list it points at.', node.of));
    return null;
  }
  const held = readable(field, problems);
  if (held === null) return null;
  if (!isNumericType(held)) {
    problems.push(problem('FORMULA_TYPE_MISMATCH', `Only numbers can be totalled, and "${field.label}" holds ${held}.`, node.of));
    return null;
  }
  return held;
};

/**
 * What arithmetic on two numbers answers.
 *
 * Money times money is refused: an amount multiplied by an amount is not an
 * amount, and no shop means it. Money divided by money IS a plain number, which
 * is what a margin is, so that one is allowed and it loses the currency.
 */
const calcType = (
  op: FormulaCalcOp,
  left: FormulaValueType,
  right: FormulaValueType,
  problems: FormulaProblem[],
): FormulaValueType | null => {
  const moneyLeft = left === 'money';
  const moneyRight = right === 'money';
  if (moneyLeft && moneyRight) {
    if (op === 'divide') return 'number';
    if (op === 'add' || op === 'subtract') return 'money';
    problems.push(problem('FORMULA_TYPE_MISMATCH', 'An amount times an amount is not an amount.'));
    return null;
  }
  if (moneyLeft || moneyRight) {
    if (op === 'add' || op === 'subtract') {
      problems.push(problem('FORMULA_TYPE_MISMATCH', 'Only an amount may be added to an amount.'));
      return null;
    }
    if (op === 'divide' && moneyRight) {
      problems.push(problem('FORMULA_TYPE_MISMATCH', 'A plain number divided by an amount is not an amount.'));
      return null;
    }
    return 'money';
  }
  return widerNumber(left, right);
};

/** Quantity survives a mix with a plain number, because the unit still means something. */
const widerNumber = (left: FormulaValueType, right: FormulaValueType): FormulaValueType =>
  left === 'quantity' || right === 'quantity' ? 'quantity' : 'number';

/** Money outranks a quantity, which outranks a bare number. */
const richerNumber = (left: FormulaValueType, right: FormulaValueType): FormulaValueType => {
  if (left === 'money' || right === 'money') return 'money';
  return widerNumber(left, right);
};

const comparable = (op: FormulaTestOp, left: FormulaValueType, right: FormulaValueType): boolean => {
  if (op === 'eq' || op === 'ne') {
    if (left === right) return true;
    return isNumericType(left) && isNumericType(right);
  }
  if (left === 'date' && right === 'date') return true;
  return isNumericType(left) && isNumericType(right);
};

/* ------------------------------------------------------------- reading it back */

/**
 * A stored spec, read back without trusting it.
 *
 * Null means the document no longer parses: a hand-edited row, or a shape from
 * a future version. A formula nobody can read must not be evaluated, and it
 * must not take the whole row down either. `readTrigger` in links.ts answers
 * null for the same reason, and every caller treats null as "serve nothing".
 */
export const readFormulaSpec = (raw: unknown): FormulaSpec | null => {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const held = raw as { result?: unknown; expr?: unknown; onMissing?: unknown };
  if (typeof held.result !== 'string' || !(held.result in RESULT_FITS)) return null;
  const expr = readNode(held.expr, 0);
  if (expr === null) return null;
  const onMissing = held.onMissing === 'zero' ? 'zero' : 'blank';
  return { result: held.result as FormulaResultType, expr, onMissing };
};

/**
 * `app.field.spec` as a whole, which is keyed by field type.
 *
 * Two shapes: a formula's `{formula}`, or a choice's `{filter?, maxChosen?}`
 * with at least one key. A key that is there but does not read makes the whole
 * spec null: dropping it and keeping the rest would store, or serve, a spec
 * nobody sent. A spec that holds `formula` is a formula's
 * and nothing else, so a formula that no longer parses is null rather than an
 * empty choice. `maxChosen` reads as any whole number; `filterProblems` is
 * what holds it to 1 to 20.
 */
export const readFieldSpec = (raw: unknown): FieldSpec | null => {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const held = raw as { formula?: unknown; lines?: unknown; filter?: unknown; maxChosen?: unknown };
  if (held.formula !== undefined) {
    const formula = readFormulaSpec(held.formula);
    return formula === null ? null : { formula };
  }
  if (held.lines !== undefined) {
    const lines = readLinesSpec(held.lines);
    return lines === null ? null : { lines };
  }
  const choice: { filter?: ChoiceFilter; maxChosen?: number } = {};
  if (held.filter !== undefined) {
    const filter = readChoiceFilter(held.filter);
    if (filter === null) return null;
    choice.filter = filter;
  }
  if (held.maxChosen !== undefined) {
    if (typeof held.maxChosen !== 'number' || !Number.isInteger(held.maxChosen)) return null;
    choice.maxChosen = held.maxChosen;
  }
  return choice.filter === undefined && choice.maxChosen === undefined ? null : choice;
};

/**
 * A stored `lines` spec, read back without trusting it. `countFrom` absent
 * reads as null; `max` reads as any whole number of at least 1, and the
 * planner is what holds it to `ENTRY_BATCH_MAX`.
 */
export const readLinesSpec = (raw: unknown): LinesFieldSpec | null => {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const held = raw as { recordId?: unknown; countFrom?: unknown; max?: unknown };
  if (typeof held.recordId !== 'string' || held.recordId === '') return null;
  const countFrom = held.countFrom === undefined || held.countFrom === null ? null : held.countFrom;
  if (countFrom !== null && (typeof countFrom !== 'string' || countFrom === '')) return null;
  if (typeof held.max !== 'number' || !Number.isInteger(held.max) || held.max < 1) return null;
  return { recordId: held.recordId, countFrom, max: held.max };
};

/** A stored filter, read back without trusting it. */
export const readChoiceFilter = (raw: unknown): ChoiceFilter | null => {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const held = raw as { field?: unknown; equals?: unknown };
  return typeof held.field === 'string' && held.field !== '' && typeof held.equals === 'string' && held.equals !== ''
    ? { field: held.field, equals: held.equals }
    : null;
};

/* ---------------------------------------------------------- dependent choices */

/**
 * Whether one row of the target list may be offered, or kept, given this
 * entry's values. The one rule: the phone's picker, the web's popover and the
 * server's write all call it.
 *
 * `targetRow` is the target row's values keyed by field id (a picker that only
 * holds `RecordTargetRow.filterValue` passes `{[filter.field]: filterValue}`).
 * `values` is this entry's values.
 *
 *   `equals` empty            every row matches: nothing has been chosen yet.
 *   `equals` one row id       the row's `field` is that id.
 *   `equals` a list of ids    the row's `field` is one of them (a multi_select).
 */
export const rowMatches = (
  filter: ChoiceFilter,
  targetRow: FormulaValues,
  values: FormulaValues,
): boolean => {
  const wanted = values[filter.equals];
  const parents =
    typeof wanted === 'string'
      ? wanted === ''
        ? []
        : [wanted]
      : Array.isArray(wanted)
        ? wanted.filter((one): one is string => typeof one === 'string' && one !== '')
        : [];
  if (parents.length === 0) return true;
  const own = targetRow[filter.field];
  return typeof own === 'string' && parents.includes(own);
};

/** What `filterProblems` needs to know of one field. */
export type ChoiceFieldInfo = {
  readonly id: string;
  readonly type: string;
  /** The list a reference or multi_select points at. Null on every other type. */
  readonly targetRecordId: string | null;
  /** This field's own filter, when it has one. Only the ring walk reads it. */
  readonly filter?: ChoiceFilter | null;
};

/**
 * How `filterProblems` asks about fields. Synchronous, like `FormulaLookup`.
 *
 * `own` answers for this record. `onTarget` answers for a field on another
 * record, the list this field points at. `above` answers, on a lines record
 * only, for a field of the record its parent reference points at: a line's
 * filter may follow a field of the row it hangs from. Absent on every other
 * record, and then an `equals` that is not on this record is unknown.
 */
export type ChoiceLookup = {
  readonly own: (fieldId: string) => ChoiceFieldInfo | null;
  readonly onTarget: (targetRecordId: string, fieldId: string) => ChoiceFieldInfo | null;
  readonly above?: (fieldId: string) => ChoiceFieldInfo | null;
};

const CHOICE_TYPES: ReadonlySet<string> = new Set(['reference', 'multi_select']);

/**
 * Every problem with the filter and limit of one field, at once: the shapes and
 * the list matches. It does not judge rows, which is `rowMatches`.
 *
 * A filter and a default are not a ring: Category may start with
 * `hop sub_category.category` while Sub-category filters by Category, because
 * a default never overwrites what a person typed. Only filters are walked here.
 */
export const filterProblems = (
  field: ChoiceFieldInfo,
  spec: ChoiceSpec,
  lookup: ChoiceLookup,
): readonly FormulaProblem[] => {
  const problems: FormulaProblem[] = [];
  if (!CHOICE_TYPES.has(field.type)) {
    if (spec.filter !== undefined || spec.maxChosen !== undefined) {
      problems.push(problem('FILTER_NOT_CHOICE', 'Only a field that points at a list can be filtered or limited.', field.id));
    }
    return problems;
  }

  if (spec.maxChosen !== undefined) {
    if (field.type !== 'multi_select') {
      problems.push(problem('FILTER_MAX_CHOSEN', 'Only a field that holds several choices can have a limit.', field.id));
    } else if (!Number.isInteger(spec.maxChosen) || spec.maxChosen < 1 || spec.maxChosen > CHOICE_MAX_CHOSEN) {
      problems.push(
        problem('FILTER_MAX_CHOSEN', `The limit must be a whole number from 1 to ${CHOICE_MAX_CHOSEN}.`, field.id),
      );
    }
  }

  const filter = spec.filter;
  if (filter === undefined) return problems;

  if (filter.equals === field.id) {
    problems.push(problem('FILTER_CYCLE', 'A field cannot be filtered by itself.', field.id));
    return problems;
  }

  // `field`: a reference on the list this field points at.
  let fieldTarget: string | null = null;
  const onTarget = field.targetRecordId === null ? null : lookup.onTarget(field.targetRecordId, filter.field);
  if (onTarget === null) {
    problems.push(problem('FILTER_FIELD_UNKNOWN', 'That field is not on the list this field points at.', filter.field));
  } else if (onTarget.type !== 'reference') {
    problems.push(
      problem('FILTER_FIELD_NOT_REFERENCE', 'A list can only be filtered by a field that points at another list.', filter.field),
    );
  } else {
    fieldTarget = onTarget.targetRecordId;
  }

  // `equals`: a reference or a multi_select on this row, or, on a line, on
  // the row the line hangs from.
  let equalsTarget: string | null = null;
  const equals = lookup.own(filter.equals) ?? lookup.above?.(filter.equals) ?? null;
  if (equals === null) {
    problems.push(problem('FILTER_EQUALS_UNKNOWN', 'That field is not on this record.', filter.equals));
  } else if (!CHOICE_TYPES.has(equals.type)) {
    problems.push(
      problem('FILTER_EQUALS_NOT_CHOICE', 'A filter can only follow a field that points at a list.', filter.equals),
    );
  } else {
    equalsTarget = equals.targetRecordId;
  }

  if (fieldTarget !== null && equalsTarget !== null && fieldTarget !== equalsTarget) {
    problems.push(problem('FILTER_LIST_MISMATCH', 'The two fields of a filter must point at the same list.', filter.equals));
  }

  // A ring among filters: Sub-category follows Category, Category follows
  // Sub-category. Walk the `equals` chain on this record until it ends or
  // comes back. `seen` keeps a ring that does not pass through this field
  // from walking forever.
  const seen = new Set<string>([field.id]);
  let next: string | undefined = filter.equals;
  while (next !== undefined && !seen.has(next)) {
    seen.add(next);
    next = lookup.own(next)?.filter?.equals ?? undefined;
  }
  if (next === field.id) {
    problems.push(problem('FILTER_CYCLE', 'These fields filter each other in a ring.', field.id));
  }

  return problems;
};

const CALC_OPS: ReadonlySet<string> = new Set(['add', 'subtract', 'multiply', 'divide']);
const AGG_OPS: ReadonlySet<string> = new Set(['sum', 'count', 'min', 'max']);
const TEST_OPS: ReadonlySet<string> = new Set(['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'filled', 'empty']);

const readNode = (raw: unknown, depth: number): FormulaNode | null => {
  if (depth > FORMULA_MAX_DEPTH) return null;
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const held = raw as Record<string, unknown>;
  const kind = held.kind;

  if (kind === 'field') {
    return typeof held.fieldId === 'string' && held.fieldId !== ''
      ? { kind: 'field', fieldId: held.fieldId }
      : null;
  }

  if (kind === 'hop') {
    if (typeof held.via !== 'string' || held.via === '') return null;
    if (typeof held.fieldId !== 'string' || held.fieldId === '') return null;
    return { kind: 'hop', via: held.via, fieldId: held.fieldId };
  }

  if (kind === 'const') {
    const value = held.value;
    if (typeof value === 'number') return Number.isFinite(value) ? { kind: 'const', value } : null;
    if (typeof value === 'string' || typeof value === 'boolean') return { kind: 'const', value };
    return null;
  }

  if (kind === 'calc') {
    if (typeof held.op !== 'string' || !CALC_OPS.has(held.op)) return null;
    const left = readNode(held.left, depth + 1);
    const right = readNode(held.right, depth + 1);
    if (left === null || right === null) return null;
    return { kind: 'calc', op: held.op as FormulaCalcOp, left, right };
  }

  if (kind === 'round') {
    const of = readNode(held.of, depth + 1);
    if (of === null) return null;
    if (typeof held.decimals !== 'number' || !Number.isFinite(held.decimals)) return null;
    return { kind: 'round', of, decimals: Math.min(8, Math.max(0, Math.trunc(held.decimals))) };
  }

  if (kind === 'if') {
    if (typeof held.op !== 'string' || !TEST_OPS.has(held.op)) return null;
    const op = held.op as FormulaTestOp;
    const left = readNode(held.left, depth + 1);
    const then = readNode(held.then, depth + 1);
    const otherwise = readNode(held.otherwise, depth + 1);
    if (left === null || then === null || otherwise === null) return null;
    if (!testNeedsRight(op)) return { kind: 'if', left, op, then, otherwise };
    const right = readNode(held.right, depth + 1);
    return right === null ? null : { kind: 'if', left, op, right, then, otherwise };
  }

  if (kind === 'days_between') {
    const from = readNode(held.from, depth + 1);
    const to = readNode(held.to, depth + 1);
    return from === null || to === null ? null : { kind: 'days_between', from, to };
  }

  if (kind === 'concat') {
    if (!Array.isArray(held.parts) || held.parts.length === 0) return null;
    const parts: FormulaNode[] = [];
    for (const one of held.parts) {
      const read = readNode(one, depth + 1);
      if (read === null) return null;
      parts.push(read);
    }
    return { kind: 'concat', parts };
  }

  if (kind === 'coalesce') {
    // Two is the smallest number of choices that IS a choice. One is a value
    // wearing a fallback's clothes, and it reads back as nonsense.
    if (!Array.isArray(held.of) || held.of.length < 2) return null;
    const of: FormulaNode[] = [];
    for (const one of held.of) {
      const read = readNode(one, depth + 1);
      if (read === null) return null;
      of.push(read);
    }
    return { kind: 'coalesce', of };
  }

  if (kind === 'agg') {
    if (typeof held.op !== 'string' || !AGG_OPS.has(held.op)) return null;
    if (typeof held.lines !== 'string' || held.lines === '') return null;
    const op = held.op as FormulaAggOp;
    if (op === 'count') return held.of === undefined ? { kind: 'agg', op, lines: held.lines } : null;
    if (typeof held.of !== 'string' || held.of === '') return null;
    return { kind: 'agg', op, lines: held.lines, of: held.of };
  }

  return null;
};

/**
 * One node, read back on its own.
 *
 * `readFormulaSpec` is the whole document; this is the tree inside it, for the
 * callers that hold a tree without a result type of its own — an insight
 * measure, a link mapping being previewed, a wire node's payload.
 */
export const readFormulaNode = (raw: unknown): FormulaNode | null => readNode(raw, 0);

/** True when the string is one of the four operators. For a reader off the wire. */
export const isFormulaCalcOp = (value: unknown): value is FormulaCalcOp =>
  typeof value === 'string' && CALC_OPS.has(value);

/** True when the string is one of the four things an `agg` works out. */
export const isFormulaAggOp = (value: unknown): value is FormulaAggOp =>
  typeof value === 'string' && AGG_OPS.has(value);

/** True when the string is one of the eight questions an `if` may ask. */
export const isFormulaTestOp = (value: unknown): value is FormulaTestOp =>
  typeof value === 'string' && TEST_OPS.has(value);

/* ------------------------------------------------------------- what it reads */

/**
 * Every field on THIS record the formula reads, including the `via` of a hop.
 *
 * The cycle check walks these, and the structure editor refuses to archive one.
 * `sourceFieldsRead` in links.ts is the same function for the same reason.
 */
export const formulaFieldsRead = (spec: FormulaSpec): readonly string[] => {
  const out = new Set<string>();
  walk(spec.expr, (node) => {
    if (node.kind === 'field') out.add(node.fieldId);
    if (node.kind === 'hop') out.add(node.via);
    if (node.kind === 'agg') out.add(node.lines);
  });
  return [...out];
};

/**
 * Every field an `agg` totals over, with the fields it reads on each row.
 *
 * The server loads the chosen rows of each multi_select up front, and has the
 * lines of a `lines` field in hand from the write, so `evaluateFormula` stays
 * synchronous and shared with the device. `fields` is empty for a field that
 * is only counted.
 */
export const formulaLinesRead = (spec: FormulaSpec): readonly { lines: string; fields: readonly string[] }[] => {
  const out = new Map<string, Set<string>>();
  walk(spec.expr, (node) => {
    if (node.kind !== 'agg') return;
    const fields = out.get(node.lines) ?? new Set<string>();
    if (node.of !== undefined) fields.add(node.of);
    out.set(node.lines, fields);
  });
  return [...out].map(([lines, fields]) => ({ lines, fields: [...fields] }));
};

/**
 * Every hop the formula makes, as `via` to the field it reads.
 *
 * The server loads these up front, one query per pair, so `evaluateFormula`
 * stays synchronous and shared with the device. `loadReferenceValues` in
 * apps/server/src/links.ts already does exactly this for a Link.
 */
export const formulaHopsRead = (spec: FormulaSpec): readonly { via: string; fieldId: string }[] => {
  const seen = new Set<string>();
  const out: { via: string; fieldId: string }[] = [];
  walk(spec.expr, (node) => {
    if (node.kind !== 'hop') return;
    const key = `${node.via} ${node.fieldId}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ via: node.via, fieldId: node.fieldId });
  });
  return out;
};

/**
 * The recipe with every field it reads named through `ids`; a field `ids`
 * does not name stays as it is.
 *
 * A change that adds a field and a recipe reading it names the field by the
 * handle of that one change. The planner swaps the handle for the id it mints,
 * and the phone's form swaps the card's handle for its own, so both ends keep
 * the recipe pointing at the field beside it. A hop's `via` is renamed too,
 * because it is a field of this record like any other read.
 */
export const formulaWithFieldIds = (spec: FormulaSpec, ids: ReadonlyMap<string, string>): FormulaSpec => {
  const swap = (node: FormulaNode): FormulaNode => {
    switch (node.kind) {
      case 'field':
        return { ...node, fieldId: ids.get(node.fieldId) ?? node.fieldId };
      case 'hop':
        return { ...node, via: ids.get(node.via) ?? node.via };
      case 'const':
        return node;
      case 'calc':
        return { ...node, left: swap(node.left), right: swap(node.right) };
      case 'round':
        return { ...node, of: swap(node.of) };
      case 'if':
        return {
          ...node,
          left: swap(node.left),
          ...(node.right === undefined ? {} : { right: swap(node.right) }),
          then: swap(node.then),
          otherwise: swap(node.otherwise),
        };
      case 'days_between':
        return { ...node, from: swap(node.from), to: swap(node.to) };
      case 'concat':
        return { ...node, parts: node.parts.map(swap) };
      case 'coalesce':
        return { ...node, of: node.of.map(swap) };
      // `lines` is a field of this record; `of` is a field of the lines, which no add renames.
      case 'agg':
        return { ...node, lines: ids.get(node.lines) ?? node.lines };
    }
  };
  return { ...spec, expr: swap(spec.expr) };
};

const walk = (node: FormulaNode, visit: (node: FormulaNode) => void): void => {
  visit(node);
  for (const kid of childrenOf(node)) walk(kid, visit);
};

/* ------------------------------------------------------------- the fill order */

/**
 * The order the record's formulas have to be worked out in.
 *
 * A formula may read another formula on the same row, so filling them in the
 * order they happen to be stored would feed a stale answer into the next one.
 * Both sides sort by this: `applyFormulas` on the server and `fillFormulas` on
 * the device. One function, so the two orders cannot drift.
 *
 * Only reads of the record's OWN formulas are edges. A hop lands on another
 * record, whose fill is that record's own business, and a read of a plain
 * stored field is no edge at all because nothing has to run first.
 *
 * Ties keep the order they came in, which is the order the fields are in. A
 * shuffled answer would make every diff of a fill look like a change.
 */
export const formulaOrder = (
  formulas: readonly { fieldId: string; spec: FormulaSpec }[],
): { ok: true; order: readonly string[] } | { ok: false; cycle: readonly string[] } => {
  const ids = formulas.map((one) => one.fieldId);
  const known = new Set(ids);
  const needs = new Map<string, readonly string[]>();
  for (const one of formulas) {
    needs.set(
      one.fieldId,
      formulaFieldsRead(one.spec).filter((fieldId) => known.has(fieldId)),
    );
  }

  const order: string[] = [];
  const placed = new Set<string>();
  // A pass in input order, repeated while it still places something. Quadratic
  // in the number of formulas on one record, which is a number under twenty,
  // and stable by construction — which a queue would not be.
  for (let moved = true; moved; ) {
    moved = false;
    for (const fieldId of ids) {
      if (placed.has(fieldId)) continue;
      if (!(needs.get(fieldId) ?? []).every((one) => placed.has(one))) continue;
      placed.add(fieldId);
      order.push(fieldId);
      moved = true;
    }
  }

  if (order.length === ids.length) return { ok: true, order };
  // Whatever is left is in a ring, or reads one. Naming all of it is more use
  // to a person than naming one edge of it.
  return { ok: false, cycle: ids.filter((fieldId) => !placed.has(fieldId)) };
};
